import React, { useState, useEffect } from "react";

import { Formik, Form } from "formik";

import cdesApi from "apis/cdeDefinitions";
import usersApi from "apis/users";
import { Button, Pane, Typography, PageLoader } from "clearsense-ui/components";
import {
  Input,
  Textarea,
  Select,
  Checkbox,
  Radio,
} from "clearsense-ui/components/formik";

import {
  CREATE_CDE_INITIAL_VALUES,
  CREATE_CDE_VALIDATION_SCHEMA,
} from "../constants/newCDE";

const NewCDE = ({
  isPaneOpen,
  setIsPaneOpen,
  fetchCdesData,
  setCheckedRows,
  setIsTableLoading,
  isEdit,
  editedRow,
  filterParams,
}) => {
  const [stewards, setStewards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editData, setEditData] = useState({});
  const [isSaveAndAdd, setIsSaveAndAdd] = useState(false);

  const { setCurrentPage, setSearch } = filterParams;

  const getEditedCDE = async () => {
    try {
      const { data } = await cdesApi.getCde(editedRow.id);
      setEditData(data);
    } catch (error) {
      logger.log(error);
    }
  };

  const onSubmit = async (values, { resetForm }) => {
    try {
      setIsTableLoading(true);
      const payload = {
        name: values.name,
        description: values.description,
        drafter_id: values.drafter.value,
        approver_id: values.approver.value,
        is_secured: values.isSecure ? values.secureCde : "",
        domain_id: editData?.domain ? editData?.domain?.id : null,
      };
      isEdit
        ? await cdesApi.editCde(editData.id, payload)
        : await cdesApi.createCde(payload);
      await fetchCdesData();
      setCurrentPage(1);
      setCheckedRows([]);
      setSearch("");

      if (isSaveAndAdd) {
        resetForm();
      } else setIsPaneOpen(false);
    } catch (error) {
      logger.log(error);
    } finally {
      setEditData({});
      setIsTableLoading(false);
    }
  };

  const fetchStewards = async () => {
    try {
      const { data } = await usersApi.fetch(1, 1000, "");
      setStewards(
        data?.users?.map(user => ({ label: user.name, value: user.key }))
      );
      setIsLoading(false);
    } catch (error) {
      logger.log(error);
    }
  };

  const toggleIsSecured = (setFieldValue, values) => {
    values.isSecure && setFieldValue("secureCde", "");
    setFieldValue("isSecure", !values.isSecure);
  };

  useEffect(() => {
    fetchStewards();
  }, []);

  useEffect(() => {
    isEdit && editedRow.id && getEditedCDE();
  }, [isEdit, editedRow]);

  return (
    <Pane isOpen={isPaneOpen} onClose={() => setIsPaneOpen(false)}>
      <Pane.Header>
        <Typography style="h3" type="bold" className="-mt-3">
          {isEdit ? "Edit CDE" : "Create a CDE"}
        </Typography>
        <Typography style="large">Description</Typography>
      </Pane.Header>
      <Formik
        initialValues={
          isEdit
            ? {
                name: editData?.name,
                description: editData?.description,
                drafter: {
                  label: editData?.drafter?.name,
                  value: editData?.drafter?.id,
                },
                approver: {
                  label: editData?.approver?.name,
                  value: editData?.approver?.id,
                },
                secureCde: editData?.is_secured,
                isSecure: editData?.is_secured === "" ? false : true,
              }
            : CREATE_CDE_INITIAL_VALUES
        }
        validationSchema={CREATE_CDE_VALIDATION_SCHEMA}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ setFieldValue, values }) => (
          <Form noValidate>
            {isLoading ? (
              <PageLoader />
            ) : (
              <>
                <Pane.Body>
                  <div className="py-8 flex w-full flex-col gap-4 overflow-auto">
                    <Input
                      label="Name"
                      name="name"
                      placeholder="Enter CDE name"
                      data-cy="cde-name-input"
                      required
                    />
                    <Textarea
                      label="Description"
                      name="description"
                      placeholder="Enter description"
                      data-cy="cde-description-input"
                    />
                    <Select
                      label="Drafter"
                      placeholder="Select Steward"
                      name="drafter"
                      options={stewards}
                      strategy="fixed"
                      required
                    />
                    <Select
                      label="Approver"
                      placeholder="Select Steward"
                      name="approver"
                      strategy="fixed"
                      options={stewards}
                      required
                    />
                    <Checkbox
                      label="Is Secured"
                      name="isSecure"
                      data-cy="secure-cde-checkbox"
                      value={values.isSecure}
                      onChange={() => toggleIsSecured(setFieldValue, values)}
                    />
                    <div className="flex gap-4">
                      <Radio name="secureCde" required>
                        <Radio.Item
                          data-cy="secure-cde-phi-radio"
                          label="Is PHI"
                          value="phi"
                          name="secureCde"
                          disabled={!values.isSecure}
                        />
                        <Radio.Item
                          data-cy="secure-cde-business-sensitive-radio"
                          label="Is Business Sensitive"
                          value="businessSensitive"
                          name="secureCde"
                          disabled={!values.isSecure}
                        />
                      </Radio>
                    </div>
                  </div>
                </Pane.Body>
                <Pane.Footer className="gap-1">
                  {!isEdit && (
                    <Button
                      label="Save and Add More"
                      type="submit"
                      onClick={() => setIsSaveAndAdd(true)}
                      data-cy="cde-save-and-add-more-button"
                    />
                  )}
                  <Button
                    label="Save and Close"
                    type="submit"
                    onClick={() => setIsSaveAndAdd(false)}
                    data-cy="cde-save-and-close-button"
                  />
                  <Button
                    label="Cancel"
                    style="secondary"
                    data-cy="cde-cancel-button"
                    onClick={() => setIsPaneOpen(false)}
                  />
                </Pane.Footer>
              </>
            )}
          </Form>
        )}
      </Formik>
    </Pane>
  );
};
export default NewCDE;
